<template>
  <div v-if="hasChargerLabel" class="d-flex align-center ga-2">
    <img
      v-if="chargerImageUrl"
      :src="chargerImageUrl"
      :alt="chargerText"
      height="28"
      :class="{ 'cursor-pointer': canOpenDialog }"
      @click="canOpenDialog && (showChargerDialog = true)"
    />

    <sf-link v-if="canOpenDialog" href="#" @click.prevent="showChargerDialog = true">
      {{ chargerText }}
    </sf-link>

    <v-tooltip v-else-if="chargerText" location="bottom" interactive :aria-label="chargerText">
      <template #activator="{ props: tooltipProps }">
        <span v-bind="tooltipProps" class="text-primary cursor-pointer">
          {{ chargerText }}
        </span>
      </template>

      <span>{{ tooltipText }}</span>
    </v-tooltip>
  </div>

  <v-dialog v-model="showChargerDialog" max-width="450">
    <v-card color="white">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>{{ chargerText }}</span>
        <v-btn icon variant="text" @click="showChargerDialog = false">
          <v-icon :icon="mdiClose" />
        </v-btn>
      </v-card-title>

      <sf-alert-base v-if="isChargerExcluded" color="blue-grey" class="mx-2 mt-1">
        <div class="d-flex align-center ga-2">
          <v-icon :icon="mdiInformationOutline" size="large" />
          <span>{{ t('charger.chargerNotIncludedHint') }}</span>
        </div>
      </sf-alert-base>
      <v-card-text class="pa-6 d-flex align-center justify-center">
        <img
          v-if="chargerInformationUrl"
          :src="chargerInformationUrl"
          :alt="chargerText"
          style="max-width: 90vw; max-height: 90vh; width: auto; height: auto"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import SfAlertBase from '@/components/alerts/SfAlertBase.vue'
import SfLink from '@/components/text/SfLink.vue'
import { ChargerInfo } from '@/types/index'
import { mdiClose, mdiInformationOutline } from '@mdi/js'
import { computed, ref, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const CHARGER_INCLUDED_FILE_NAME = 'included.jpeg'
const CHARGER_EXCLUDED_FILE_NAME = 'excluded.jpeg'

const { t } = useI18n()

const props = defineProps({
  chargerInfo: {
    type: Object as PropType<ChargerInfo | null>,
    default: null
  }
})

const showChargerDialog = ref(false)

const chargerIncludedUrl = computed(() => props.chargerInfo?.chargerIncludedUrl ?? '')
const chargerInformationUrl = computed(() => props.chargerInfo?.chargerInformationUrl ?? '')

const normalizedChargerIncludedUrl = computed(() => chargerIncludedUrl.value.toLowerCase())

const isChargerIncluded = computed(() => {
  return normalizedChargerIncludedUrl.value.includes(CHARGER_INCLUDED_FILE_NAME)
})

const isChargerExcluded = computed(() => {
  return normalizedChargerIncludedUrl.value.includes(CHARGER_EXCLUDED_FILE_NAME)
})

const hasChargerLabel = computed(() => {
  return !!chargerIncludedUrl.value || !!chargerInformationUrl.value
})

const chargerImageUrl = computed(() => {
  return chargerIncludedUrl.value || chargerInformationUrl.value
})

const chargerText = computed(() => {
  if (isChargerExcluded.value) {
    return t('charger.noCharger')
  }
  if (isChargerIncluded.value || chargerInformationUrl.value) {
    return t('charger.charger')
  }
  return ''
})

const tooltipText = computed(() => {
  if (isChargerExcluded.value) {
    return t('charger.chargerNotIncludedTooltip')
  }
  if (isChargerIncluded.value) {
    return t('charger.chargerIncludedTooltip')
  }
  return ''
})

const canOpenDialog = computed(() => {
  return !!chargerInformationUrl.value && !!chargerText.value
})
</script>
